import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCollections } from '../../contexts/CollectionsContext';
import { PremiumBadge } from '../PremiumGate';

/**
 * CollectionsPicker component for selecting which collections a list belongs to.
 * Used in list create/edit forms. Premium-only feature.
 */
function CollectionsPicker({
  canAccessCollections,
  selectedCollectionIds,
  onToggleCollection,
  onClose,
  listId, // Optional - used in edit mode to show current collections
}) {
  const navigate = useNavigate();
  const { collections, loading } = useCollections();

  // Sort collections by position/title
  const sortedCollections = useMemo(() => {
    return [...collections].sort((a, b) => {
      if (a.position !== undefined && b.position !== undefined) {
        return a.position - b.position;
      }
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [collections]);

  // If user doesn't have access, show upgrade prompt
  if (!canAccessCollections) {
    return (
      <div className="form-group premium-locked-group">
        <label>
          Collections
          <PremiumBadge small />
        </label>
        <div className="premium-locked-content">
          <p className="premium-locked-text">Organize your lists into collections</p>
          <button
            type="button"
            className="btn-upgrade-small"
            onClick={() => {
              onClose?.();
              navigate('/settings/premium');
            }}
          >
            Upgrade to Premium
          </button>
        </div>
      </div>
    );
  }

  // No collections yet
  if (!loading && collections.length === 0) {
    return (
      <div className="form-group">
        <label>
          Collections
          <span className="optional-label"> (optional)</span>
        </label>
        <div className="collections-picker-empty">
          <p>You haven't created any collections yet.</p>
          <button
            type="button"
            className="btn-create-collection-small"
            onClick={() => {
              onClose?.();
              navigate('/lists?tab=collections&create=true');
            }}
          >
            Create a Collection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-group">
      <label>
        Collections
        <span className="optional-label"> (optional)</span>
        {selectedCollectionIds.size > 0 && (
          <span className="collections-selected-count">
            {selectedCollectionIds.size} selected
          </span>
        )}
      </label>

      {loading ? (
        <div className="collections-picker-loading">Loading collections...</div>
      ) : (
        <div className="collections-picker-list">
          {sortedCollections.map((collection) => {
            const isSelected = selectedCollectionIds.has(collection.id);
            return (
              <button
                key={collection.id}
                type="button"
                className={`collections-picker-item ${isSelected ? 'selected' : ''}`}
                onClick={() => onToggleCollection(collection.id)}
              >
                <div className={`collections-picker-checkbox ${isSelected ? 'checked' : ''}`}>
                  {isSelected && (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div className="collections-picker-info">
                  <span className="collections-picker-title">{collection.title}</span>
                  <span className="collections-picker-meta">
                    {(collection.listIds?.length || 0)} {(collection.listIds?.length || 0) === 1 ? 'list' : 'lists'}
                  </span>
                </div>
                {collection.visibility === 'private' && (
                  <svg className="collections-picker-private-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}

      <p className="field-hint">
        Add this list to collections for better organization. Lists can belong to multiple collections.
      </p>
    </div>
  );
}

export default CollectionsPicker;
